import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { FadeInSection } from '../../../components/ui'
import { fetchPlans } from '../../../lib/billingApi'
import { upgradesEnabled } from '../../../lib/featureFlags'
import { savePendingPlan } from '../../../lib/pendingPlan'
import type { Plan } from '../../../types/api'

const PAID_FEATURES = [
  'Full nudge sequence',
  'Conversation-Aware AI',
  'Recovery alerts',
]

const ENTERPRISE_DEFAULT_UNITS = 3
const ENTERPRISE_FALLBACK_MIN = 2
const ENTERPRISE_FALLBACK_MAX = 50

const PLAN_BG = ['#FBE6D4', '#FFE8C2', '#111', '#E7F0E3']

function CheckIcon({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="flex-shrink-0 mt-0.5">
      <path
        d="M5 13l4 4L19 7"
        stroke={color}
        strokeWidth="2.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function DashIcon({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="flex-shrink-0 mt-0.5">
      <path d="M5 12h14" stroke={color} strokeWidth="2.75" strokeLinecap="round" />
    </svg>
  )
}

function meteredMonthlyDollars(plan: Plan, units: number, cycle: 'MONTHLY' | 'ANNUAL'): number {
  const perUnitCents = cycle === 'ANNUAL'
    ? (plan.unitAnnualCents ?? 0) / 12
    : (plan.unitMonthlyCents ?? 0)
  return (perUnitCents * units) / 100
}

function fixedMonthlyDollars(plan: Plan, cycle: 'MONTHLY' | 'ANNUAL'): number {
  if (cycle === 'ANNUAL') return Math.round(plan.annualCents / 12) / 100
  return plan.monthlyCents / 100
}

export default function PricingSection() {
  const shouldReduceMotion = useReducedMotion()
  const [cycle, setCycle] = useState<'MONTHLY' | 'ANNUAL'>('MONTHLY')
  const [plans, setPlans] = useState<Plan[]>([])

  useEffect(() => {
    fetchPlans()
      .then((p) => setPlans(Array.isArray(p) ? p : []))
      .catch(() => { /* silently ignore */ })
  }, [])

  const freePlan = useMemo(() => plans.find(p => p.code === 'FREE'), [plans])
  const proPlan = useMemo(() => plans.find(p => p.code === 'PRO'), [plans])
  const proPlusPlan = useMemo(() => plans.find(p => p.code === 'PRO_PLUS'), [plans])
  const enterprisePlan = useMemo(() => plans.find(p => p.code === 'ENTERPRISE'), [plans])

  const minUnits = enterprisePlan?.minUnits ?? ENTERPRISE_FALLBACK_MIN
  const maxUnits = enterprisePlan?.maxUnits ?? ENTERPRISE_FALLBACK_MAX
  const unitContactSize = enterprisePlan?.unitContactSize ?? 1000

  const [enterpriseUnits, setEnterpriseUnits] = useState<number>(ENTERPRISE_DEFAULT_UNITS)

  useEffect(() => {
    if (!enterprisePlan) return
    setEnterpriseUnits(prev => Math.max(minUnits, Math.min(maxUnits, prev)))
  }, [enterprisePlan, minUnits, maxUnits])

  return (
    <section
      id="pricing"
      className="py-28 px-6"
      style={{ backgroundColor: '#FAF5EC', borderBottom: '2px solid #111' }}
    >
      <div className="max-w-6xl mx-auto">
        <FadeInSection>
          <div className="text-center mb-14">
            <span
              className="inline-block text-xs font-bold uppercase font-dm-mono"
              style={{
                backgroundColor: '#111',
                color: '#FAF5EC',
                padding: '5px 12px',
                borderRadius: 999,
                letterSpacing: '0.14em',
              }}
            >
              Pricing
            </span>
            <h2
              className="font-extrabold mt-5 tracking-tight"
              style={{
                color: '#111',
                fontSize: 'clamp(2.25rem, 5vw, 3.75rem)',
                lineHeight: 1,
                letterSpacing: '-0.03em',
              }}
            >
              Pay less than one returned
              <br />
              customer is worth.
            </h2>
            <p
              className="mt-5 text-lg max-w-xl mx-auto"
              style={{ color: '#3a2e26', lineHeight: 1.55 }}
            >
              Free to try. Scale up by the thousand when you're ready.
            </p>
          </div>

          {/* Monthly/annual toggle */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <div
              role="tablist"
              aria-label="Billing cycle"
              className="relative inline-flex items-center p-1"
              style={{
                backgroundColor: '#fff',
                border: '2px solid #111',
                borderRadius: 999,
                boxShadow: '3px 3px 0 #111',
              }}
            >
              <motion.span
                aria-hidden
                layout
                initial={false}
                animate={{ x: cycle === 'MONTHLY' ? 0 : '100%' }}
                transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                className="absolute top-1 bottom-1 left-1 rounded-full"
                style={{
                  width: 'calc(50% - 4px)',
                  backgroundColor: '#111',
                }}
              />
              <button
                role="tab"
                aria-selected={cycle === 'MONTHLY'}
                onClick={() => setCycle('MONTHLY')}
                className="relative px-5 py-2 text-sm font-bold transition-colors duration-200"
                style={{
                  color: cycle === 'MONTHLY' ? '#fff' : '#111',
                  background: 'transparent',
                  borderRadius: 999,
                  zIndex: 1,
                  minWidth: 100,
                }}
              >
                Monthly
              </button>
              <button
                role="tab"
                aria-selected={cycle === 'ANNUAL'}
                onClick={() => setCycle('ANNUAL')}
                className="relative px-5 py-2 text-sm font-bold transition-colors duration-200"
                style={{
                  color: cycle === 'ANNUAL' ? '#fff' : '#111',
                  background: 'transparent',
                  borderRadius: 999,
                  zIndex: 1,
                  minWidth: 100,
                }}
              >
                Annual
              </button>
            </div>
            <span
              className="text-xs font-bold px-3 py-1.5"
              style={{
                backgroundColor: 'var(--accent)',
                color: '#fff',
                border: '2px solid #111',
                borderRadius: 999,
              }}
            >
              Save 2 months
            </span>
          </div>
        </FadeInSection>

        {plans.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {freePlan && (
              <PlanCard
                key="free"
                bg={PLAN_BG[0]}
                title={freePlan.displayName}
                subtitle="Try Nudge free — no credit card, no time limit."
                priceDisplay="$0"
                priceSuffix="/mo"
                features={[
                  { label: `${freePlan.contactLimit.toLocaleString()} contacts`, included: true },
                  { label: 'Basic nudge sequence only', included: false },
                ]}
                cta={{ label: 'Get started free', href: '/signup' }}
                fineprint="No credit card required"
                shouldReduceMotion={shouldReduceMotion ?? false}
              />
            )}

            {proPlan && (
              <PlanCard
                key="pro"
                bg={PLAN_BG[1]}
                title={proPlan.displayName}
                subtitle="Solo operator, small café, freelancer."
                priceDisplay={`$${fixedMonthlyDollars(proPlan, cycle).toFixed(0)}`}
                priceSuffix="/mo"
                priceSubline={cycle === 'ANNUAL' ? 'billed annually' : undefined}
                features={[
                  { label: `${proPlan.contactLimit.toLocaleString()} contacts`, included: true },
                  ...PAID_FEATURES.map(f => ({ label: f, included: true })),
                ]}
                cta={upgradesEnabled
                  ? {
                      label: 'Get started',
                      href: '/signup',
                      onClick: () => savePendingPlan({ plan: 'PRO', cycle }),
                    }
                  : { label: 'Coming soon', disabled: true }}
                fineprint={upgradesEnabled
                  ? `${proPlan.trialDays}-day free trial · no card`
                  : 'Paid plans launching soon'}
                shouldReduceMotion={shouldReduceMotion ?? false}
              />
            )}

            {proPlusPlan && (
              <PlanCard
                key="pro_plus"
                bg={PLAN_BG[2]}
                title={proPlusPlan.displayName}
                subtitle="Busy salon, multi-stylist studio, growing gym."
                priceDisplay={`$${fixedMonthlyDollars(proPlusPlan, cycle).toFixed(0)}`}
                priceSuffix="/mo"
                priceSubline={cycle === 'ANNUAL' ? 'billed annually' : undefined}
                featured
                features={[
                  { label: `${proPlusPlan.contactLimit.toLocaleString()} contacts`, included: true },
                  ...PAID_FEATURES.map(f => ({ label: f, included: true })),
                ]}
                cta={upgradesEnabled
                  ? {
                      label: 'Get started',
                      href: '/signup',
                      onClick: () => savePendingPlan({ plan: 'PRO_PLUS', cycle }),
                    }
                  : { label: 'Coming soon', disabled: true }}
                fineprint={upgradesEnabled
                  ? `${proPlusPlan.trialDays}-day free trial · no card`
                  : 'Paid plans launching soon'}
                shouldReduceMotion={shouldReduceMotion ?? false}
              />
            )}

            {enterprisePlan && (
              <PlanCard
                key="enterprise"
                bg={PLAN_BG[3]}
                title={enterprisePlan.displayName}
                subtitle="Franchise, multi-location, large customer list."
                priceDisplay={`$${meteredMonthlyDollars(enterprisePlan, enterpriseUnits, cycle).toFixed(0)}`}
                priceSuffix="/mo"
                priceSubline={cycle === 'ANNUAL' ? 'billed annually' : undefined}
                slider={{
                  units: enterpriseUnits,
                  min: minUnits,
                  max: maxUnits,
                  unitContactSize,
                  onChange: setEnterpriseUnits,
                }}
                features={PAID_FEATURES.map(f => ({ label: f, included: true }))}
                cta={upgradesEnabled
                  ? {
                      label: 'Get started',
                      href: '/signup',
                      onClick: () => savePendingPlan({ plan: 'ENTERPRISE', cycle, units: enterpriseUnits }),
                    }
                  : { label: 'Coming soon', disabled: true }}
                fineprint={upgradesEnabled
                  ? `${enterprisePlan.trialDays}-day free trial · no card`
                  : 'Paid plans launching soon'}
                shouldReduceMotion={shouldReduceMotion ?? false}
              />
            )}
          </div>
        )}

        <p className="text-center text-sm mt-12" style={{ color: '#3a2e26' }}>
          Need more than {(maxUnits * unitContactSize).toLocaleString()} contacts or multi-location support?{' '}
          <a
            href="mailto:hello@nudge.app"
            style={{ color: '#111', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}
          >
            Talk to us
          </a>
        </p>

        <style>{`
          .plan-tile { transition: transform 150ms ease, box-shadow 150ms ease; }
          .plan-tile:hover {
            transform: translate(-2px, -2px);
            box-shadow: 8px 8px 0 #111 !important;
          }
          .pricing-cta { transition: transform 150ms ease, box-shadow 150ms ease; }
          .pricing-cta:hover { transform: translate(-1px, -1px); box-shadow: 3px 3px 0 #111 !important; }
          .pro-plus-slider {
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            height: 4px;
            border-radius: 999px;
            background: rgba(255,255,255,0.25);
            outline: none;
          }
          .pro-plus-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: var(--accent);
            border: 2px solid #fff;
            cursor: pointer;
          }
          .pro-plus-slider::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: var(--accent);
            border: 2px solid #fff;
            cursor: pointer;
          }
        `}</style>
      </div>
    </section>
  )
}

type Feature = { label: string; included: boolean }
type SliderProps = {
  units: number
  min: number
  max: number
  unitContactSize: number
  onChange: (n: number) => void
}

type CtaConfig =
  | { label: string; href: string; onClick?: () => void; disabled?: false }
  | { label: string; disabled: true }

function PlanCard(props: {
  bg: string
  title: string
  subtitle: string
  priceDisplay: string
  priceSuffix: string
  priceSubline?: string
  features: Feature[]
  cta: CtaConfig
  fineprint?: string
  featured?: boolean
  slider?: SliderProps
  shouldReduceMotion: boolean
}) {
  const { bg, title, subtitle, priceDisplay, priceSuffix, priceSubline, features, cta, fineprint, featured, slider } = props

  const textColor = featured ? '#fff' : '#111'
  const subColor = featured ? 'rgba(255,255,255,0.7)' : '#3a2e26'
  const fineColor = featured ? 'rgba(255,255,255,0.5)' : '#5a4736'

  return (
    <FadeInSection>
      <div
        className="plan-tile relative h-full flex flex-col"
        style={{
          backgroundColor: bg,
          color: textColor,
          border: '2px solid #111',
          borderRadius: 20,
          boxShadow: '6px 6px 0 #111',
          padding: '28px',
        }}
      >
        {featured && (
          <div
            className="absolute -top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 text-xs font-bold uppercase"
            style={{
              backgroundColor: 'var(--accent)',
              color: '#fff',
              border: '2px solid #111',
              borderRadius: 999,
              letterSpacing: '0.08em',
            }}
          >
            Most popular
          </div>
        )}

        <div className="mb-6">
          <span
            className="text-xs font-bold uppercase font-dm-mono"
            style={{ color: subColor, letterSpacing: '0.14em' }}
          >
            {title}
          </span>

          <div className="flex items-baseline gap-1.5 mt-3">
            <span
              className="font-extrabold"
              style={{
                color: textColor,
                fontSize: '3rem',
                lineHeight: 1,
                letterSpacing: '-0.03em',
              }}
            >
              {priceDisplay}
            </span>
            <span className="text-sm font-semibold" style={{ color: subColor }}>
              {priceSuffix}
            </span>
          </div>

          {priceSubline && (
            <p className="text-xs mt-1.5" style={{ color: fineColor }}>
              {priceSubline}
            </p>
          )}

          <p className="mt-4 text-sm" style={{ color: subColor, lineHeight: 1.5 }}>
            {subtitle}
          </p>
        </div>

        {slider && (
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <span
                className="text-xs uppercase font-bold font-dm-mono"
                style={{ color: subColor, letterSpacing: '0.12em' }}
              >
                Contacts
              </span>
              <span
                className="font-extrabold text-lg"
                style={{ color: textColor }}
              >
                {(slider.units * slider.unitContactSize).toLocaleString()}
              </span>
            </div>
            <input
              type="range"
              className="pro-plus-slider"
              min={slider.min}
              max={slider.max}
              step={1}
              value={slider.units}
              onChange={e => slider.onChange(Number(e.target.value))}
              aria-label="Number of contacts (thousands)"
              style={{ background: featured ? 'rgba(255,255,255,0.25)' : 'rgba(17,17,17,0.15)' }}
            />
            <div className="flex justify-between text-xs mt-1.5 font-dm-mono font-semibold" style={{ color: fineColor }}>
              <span>{(slider.min * slider.unitContactSize).toLocaleString()}</span>
              <span>{(slider.max * slider.unitContactSize).toLocaleString()}+</span>
            </div>
          </div>
        )}

        <ul className="space-y-3 mb-7 flex-1">
          {features.map((f, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 text-sm font-medium"
              style={{ color: f.included ? textColor : fineColor }}
            >
              {f.included
                ? <CheckIcon color={featured ? '#10b981' : 'var(--accent)'} />
                : <DashIcon color={fineColor} />}
              {f.label}
            </li>
          ))}
        </ul>

        {cta.disabled ? (
          <button
            type="button"
            disabled
            className="w-full py-3 text-sm font-bold text-center block"
            style={{
              backgroundColor: featured ? 'rgba(255,255,255,0.1)' : '#fff',
              color: featured ? 'rgba(255,255,255,0.5)' : '#3a2e26',
              border: '2px solid #111',
              borderRadius: 999,
              opacity: 0.6,
              cursor: 'not-allowed',
            }}
            title="Paid plans launching soon"
          >
            {cta.label}
          </button>
        ) : (
          <Link
            to={cta.href}
            onClick={cta.onClick}
            className="pricing-cta w-full py-3 text-sm font-bold text-center block"
            style={{
              backgroundColor: featured ? 'var(--accent)' : '#111',
              color: '#fff',
              border: '2px solid #111',
              borderRadius: 999,
              textDecoration: 'none',
              boxShadow: '2px 2px 0 #111',
            }}
          >
            {cta.label}
          </Link>
        )}

        {fineprint && (
          <p className="text-center text-xs mt-3 font-medium" style={{ color: fineColor }}>
            {fineprint}
          </p>
        )}
      </div>
    </FadeInSection>
  )
}
